import { FC, useMemo } from 'react';
import { Placement } from '@floating-ui/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, ButtonProps, PopoverMenu, PopoverMenuItem, usePopoverMenu } from '@frontend/design-system';
import { PurchaseDeviceType, PurchaseDeviceTypeDisplayValuesMap } from '../constants';
import { usePurchaseDeviceActions } from '../hooks/usePurchaseDeviceActions';
import { getPurchaseDeviceActionTrackingId } from '../tracking-ids';

type ActionData = {
  label: string;
  icon?: IconName;
  trackingId?: string;
  onClick: () => void;
};

interface PurchaseDevicesActionProps extends ButtonProps {
  devices: PurchaseDeviceType[];
  alwaysShowMenu?: boolean;
  locationIds?: string[];
  source: string;
  menuPlacement?: Placement;
  moreActions?: ActionData[];
}

export const PurchaseDevicesAction: FC<PurchaseDevicesActionProps> = ({
  devices,
  alwaysShowMenu = false,
  locationIds,
  menuPlacement = 'bottom-end',
  source,
  moreActions = [],
  children,
  ...buttonProps
}) => {
  const { t } = useTranslation('purchaseDevices');
  const { getTriggerProps, getMenuProps, getItemProps } = usePopoverMenu({ placement: menuPlacement });
  const { availableDevices = [], handleDevicePurchaseAction } = usePurchaseDeviceActions({ locationIds, devices });

  const actionsList = useMemo<ActionData[]>(() => {
    const devicesActions = availableDevices.map((device) => {
      const { icon, label } = PurchaseDeviceTypeDisplayValuesMap[device];
      return {
        label,
        icon,
        trackingId: getPurchaseDeviceActionTrackingId({ device, source }),
        onClick: () => handleDevicePurchaseAction(device),
      };
    });

    return [...moreActions, ...devicesActions];
  }, [source, availableDevices, handleDevicePurchaseAction, moreActions]);

  if (actionsList.length === 0) return null;

  const isSingleAction = actionsList.length === 1;

  if (isSingleAction && !alwaysShowMenu) {
    const { icon, label, onClick, trackingId } = actionsList[0];
    return (
      <Button variant='secondary' iconName={icon} onClick={onClick} trackingId={trackingId} {...buttonProps}>
        {label}
      </Button>
    );
  }

  return (
    <>
      <Button
        variant='secondary'
        iconName={isSingleAction ? 'more-small' : undefined}
        {...buttonProps}
        {...getTriggerProps()}
      >
        {!isSingleAction && (children || t('Purchase Devices'))}
      </Button>
      <PopoverMenu {...getMenuProps()}>
        {actionsList.map(({ icon, label, onClick, trackingId }, index) => (
          <PopoverMenuItem
            key={index}
            Icon={icon ? () => <Icon name={icon} size={18} css={{ color: theme.colors.neutral50 }} /> : undefined}
            {...getItemProps({ index, onClick })}
            trackingId={trackingId}
          >
            {label}
          </PopoverMenuItem>
        ))}
      </PopoverMenu>
    </>
  );
};
